import React from 'react';
import { DiffLevel, DiffLevels, DocGrpDiffInfo } from './useApiDocGrp';
import { formatDate } from 'util/formatDateTime';

/**
 * diffLevel 계산을 위한 custom hook
 */
export const useDiffLevelCalculation = (sels: string[] | undefined, diffs: DocGrpDiffInfo[] | undefined) => {
  const { diffLevel, diffLevels } = React.useMemo<{
    diffLevel: DiffLevel;
    diffLevels: DiffLevels;
  }>((): { diffLevel: DiffLevel; diffLevels: DiffLevels } => {
    // 데이터가 로딩 중이거나 없으면 undefined 반환
    if ((sels || []).length === 0 || !diffs) {
      return {
        diffLevel: undefined,
        diffLevels: undefined,
      };
    }

    const items = sels;

    const chkKoKr = items.includes('ko-KR');
    const chkEn = items.includes('en');
    const chkEnUs = items.includes('en-US');

    // 각 필드별로 차이점 분석
    const diffLevels: DiffLevels = {
      doc: 'Same',
      version: 'Same',
      updateTime: 'Same',
      expose: 'Same',
      displayTarget: 'Same',
      fileCnt: 'Same',
    };

    // 언어별 데이터 그룹화
    const koKr = diffs.find((item) => item.lang === 'ko-KR');
    const en = diffs.find((item) => item.lang === 'en');
    const enUs = diffs.find((item) => item.lang === 'en-US');

    // 각 필드별 점수 계산 (TblCompareItem의 getDiffScore 로직과 동일)
    const getDiffLevel = (koKr: any, en: any, enUs: any): DiffLevel => {
      let score = 0;
      const compcnt = (chkKoKr ? 1 : 0) + (chkEn ? 1 : 0) + (chkEnUs ? 1 : 0);

      if (chkKoKr && chkEn) {
        if (koKr === en) score += 1;
      }
      if (chkKoKr && chkEnUs) {
        if (koKr === enUs) score += 1;
      }
      if (chkEn && chkEnUs) {
        if (en === enUs) score += 1;
      }
      if (compcnt === 3) {
        if (score === 3) return 'Same';
        else if (score === 2 || score === 1) return 'Similar';
        return 'Different';
      } else if (compcnt == 2) {
        if (score === 1) return 'Same';
        return 'Different';
      } else if (compcnt == 1) {
        return 'Same';
      }

      return 'Same';
    };

    const getDocDiffLevel = (koKr: DocGrpDiffInfo, en: DocGrpDiffInfo, enUs: DocGrpDiffInfo): DiffLevel => {
      const lvls: DiffLevel[] = [];
      if (items.includes('ko-KR')) {
        if (koKr) lvls.push('Same');
        else lvls.push('Different');
      }
      if (items.includes('en')) {
        if (en) lvls.push('Same');
        else lvls.push('Different');
      }
      if (items.includes('en-US')) {
        if (enUs) lvls.push('Same');
        else lvls.push('Different');
      }
      if (lvls.length === 0) return 'Same';
      if (lvls.every((level) => level === 'Different')) return 'Different';
      if (lvls.some((level) => level === 'Different')) return 'Similar';
      return 'Same';
    };

    diffLevels.version = getDiffLevel(koKr?.version, en?.version, enUs?.version);
    diffLevels.fileCnt = getDiffLevel(koKr?.fileCnt, en?.fileCnt, enUs?.fileCnt);
    diffLevels.updateTime = getDiffLevel(
      formatDate(new Date(koKr?.updateTime)),
      formatDate(new Date(en?.updateTime)),
      formatDate(new Date(enUs?.updateTime))
    );
    diffLevels.expose = getDiffLevel(koKr?.expose, en?.expose, enUs?.expose);
    diffLevels.displayTarget = getDiffLevel(koKr?.displayTarget, en?.displayTarget, enUs?.displayTarget);
    diffLevels.doc = getDocDiffLevel(koKr, en, enUs);

    // 전체 점수 분석
    // 비교 항목 필터링.
    const allLevels = items.reduce<DiffLevel[]>((acc, cur) => {
      if (cur === 'version') {
        acc.push(diffLevels.version);
      } else if (cur === 'update') {
        acc.push(diffLevels.updateTime);
      } else if (cur === 'expose') {
        acc.push(diffLevels.expose);
      } else if (cur === 'disp-target') {
        acc.push(diffLevels.displayTarget);
      } else if (cur === 'file') {
        acc.push(diffLevels.fileCnt);
      }
      return acc;
    }, []);
    if (items.includes('ko-KR') || items.includes('en') || items.includes('en-US')) {
      allLevels.push(diffLevels.doc);
    }

    const different = allLevels.length > 0 && allLevels.every((level) => level === 'Different'); // 모두 다른 경우
    const similar = allLevels.some((level) => level === 'Similar' || level === 'Different'); // 일부만 같은 경우

    let diffLevel: DiffLevel = 'Same';
    if (different) {
      diffLevel = 'Different';
    } else if (similar) {
      diffLevel = 'Similar';
    }

    return {
      diffLevel,
      diffLevels,
    };
  }, [sels, diffs]);

  return {
    diffLevel,
    diffLevels,
  };
};
